// Contact gate (Milestone 8). Two-column hero: program pitch + value props on
// the left, the "Get started" form card on the right (visible without
// scrolling on desktop). On submit the server either emails a magic link (→
// /check-email), admits the visitor when verification is disabled (→ /intake),
// or blocks them (→ /blocked). A returning verified visitor with a saved draft
// gets a welcome-back card instead of the form. On mobile the layout stacks.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  submitContact, fetchIntakeSession, discardDraft, logoutVisitor,
} from '../store/contact-slice.js';
import { selectSettings, selectPartnerLogos } from '../store/settings-slice.js';
import ValuePropStrip from '../components/landing/ValuePropStrip.jsx';
import WelcomeBackCard from '../components/landing/WelcomeBackCard.jsx';
import ThemeModeToggle from '../components/ThemeModeToggle.jsx';
import SanbornLogo from '../components/SanbornLogo.jsx';
import LogoBar from '../components/LogoBar.jsx';

const LandingContactPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    submitting, error, blocked, sent, verified, draft, contact,
  } = useSelector((state) => state.contact);
  const settings = useSelector(selectSettings);
  const logos = useSelector(selectPartnerLogos);

  const [form, setForm] = useState({
    name: '', email: '', organization: '', role: '',
  });

  const hasResumableDraft = Boolean(verified && draft && draft.hasContent);

  // On mount, check whether this browser already holds a verified-visitor
  // session (and a saved draft) so we can welcome them back.
  useEffect(() => {
    dispatch(fetchIntakeSession());
  }, [dispatch]);

  // React to the resolved submission / session: email link → interstitial,
  // blocked → concierge, verified with nothing to resume → straight to intake.
  useEffect(() => {
    if (sent) navigate('/check-email');
    else if (blocked) navigate('/blocked');
    else if (verified && !hasResumableDraft) navigate('/intake');
  }, [sent, blocked, verified, hasResumableDraft, navigate]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(submitContact({
      name: form.name.trim(),
      email: form.email.trim(),
      organization: form.organization.trim(),
      role: form.role.trim() || undefined,
    }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: 'text.primary',
        bgcolor: 'background.default',
        // A single restrained teal glow for depth — no heavy gradient wash.
        backgroundImage: (theme) => `radial-gradient(80% 60% at 85% -10%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 60%)`,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 12, right: 16 }}>
        <ThemeModeToggle />
      </Box>
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Box
          sx={{
            display: 'grid',
            alignItems: 'start',
            columnGap: { md: 6 },
            rowGap: { xs: 4, md: 5 },
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            gridTemplateAreas: {
              xs: '"head" "form" "props"',
              md: '"head form" "props form"',
            },
          }}
        >
          <Box sx={{ gridArea: 'head' }}>
            {/* The wordmark inherits text.primary via currentColor, so it
                stays legible in both modes. */}
            <Box sx={{ color: 'text.primary', mb: 2 }}>
              <SanbornLogo height={104} href="https://sanborn.com/" />
            </Box>
            <LogoBar logos={logos} height={48} maxWidth={180} sx={{ mb: 3 }} />
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.12em' }}
            >
              {settings.program_name}
            </Typography>
            <Typography variant="h1" sx={{ mt: 1, mb: 2, fontSize: { xs: '2rem', md: '2.75rem' } }}>
              {settings.hero_headline}
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{ maxWidth: 560, fontWeight: 400, color: 'text.secondary' }}
            >
              {settings.hero_body}
            </Typography>
          </Box>

          <Box sx={{ gridArea: 'props', maxWidth: 520 }}>
            <ValuePropStrip />
          </Box>

          {hasResumableDraft ? (
            <WelcomeBackCard
              contact={contact}
              draft={draft}
              onContinue={() => navigate('/intake')}
              onStartNew={() => { dispatch(discardDraft()); navigate('/intake'); }}
              onSignOut={() => dispatch(logoutVisitor())}
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                gridArea: 'form',
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                color: 'text.primary',
                maxWidth: 480,
                width: '100%',
                justifySelf: { md: 'end' },
                border: 1,
                borderColor: 'divider',
                borderTop: 3,
                borderTopColor: 'primary.main',
                boxShadow: (t) => (t.palette.mode === 'dark'
                  ? '0 24px 48px -24px rgba(0,0,0,0.6)'
                  : '0 24px 48px -24px rgba(15,23,42,0.3)'),
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <Box>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                    Get started
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Tell us a little about you and we&apos;ll email you a secure link
                    to open the package builder.
                  </Typography>
                </Box>

                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  label="Name"
                  value={form.name}
                  onChange={handleChange('name')}
                  required
                  autoComplete="name"
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  required
                  autoComplete="email"
                  slotProps={{ htmlInput: { maxLength: 320 } }}
                />
                <TextField
                  label="Organization"
                  value={form.organization}
                  onChange={handleChange('organization')}
                  required
                  autoComplete="organization"
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
                <TextField
                  label="Role / Title (optional)"
                  value={form.role}
                  onChange={handleChange('role')}
                  autoComplete="organization-title"
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{ mt: 1 }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : 'Get Started'}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  By submitting, you agree that we may contact you about Sanborn
                  imagery products and services.
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default LandingContactPage;
